import React from 'react';
import { cn } from '../lib/utils';
import { useT } from '../i18n';

// Lerp between two RGB triples.
const lerpRgb = (a, b, t) => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

// Map score (0-100) → smooth red → yellow → green color.
const RED = [239, 68, 68];
const YELLOW = [234, 179, 8];
const GREEN = [34, 197, 94];

const scoreToColor = (score) => {
  const s = Math.max(0, Math.min(100, score));
  const [r, g, b] = s <= 50
    ? lerpRgb(RED, YELLOW, s / 50)
    : lerpRgb(YELLOW, GREEN, (s - 50) / 50);
  return `rgb(${r}, ${g}, ${b})`;
};

const scoreBucket = (score, t) => {
  if (score >= 90) return { label: t.score?.excellent || 'Excellent', cls: 'score-excellent' };
  if (score >= 75) return { label: t.score?.good || 'Good', cls: 'score-good' };
  if (score >= 50) return { label: t.score?.average || 'Average', cls: 'score-average' };
  return { label: t.score?.poor || 'Poor', cls: 'score-poor' };
};

export const ScoreRing = ({ score, size = 'md', showLabel = true }) => {
  const t = useT();
  const color = scoreToColor(score);
  const { label, cls } = scoreBucket(score, t);
  const isExceptional = score >= 90;

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
  };

  const fontSize = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl',
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={cn('score-ring', sizeClasses[size], isExceptional && 'score-ring-glow')}
        style={{
          '--score-color': color,
          '--score-percent': `${score}%`,
        }}
      >
        <span className={cn('font-heading font-bold', fontSize[size])} style={{ color }}>
          {score}
        </span>
      </div>
      {showLabel && (
        <span className={cn('issue-badge', cls)}>{label}</span>
      )}
    </div>
  );
};

export const IssueBadge = ({ priority, children }) => {
  const classes = {
    critical: 'issue-critical',
    high: 'issue-high',
    medium: 'issue-medium',
    low: 'issue-low',
    info: 'issue-low',
  };

  return (
    <span className={cn('issue-badge', classes[priority] || classes.low)}>
      {children}
    </span>
  );
};

export const LoadingSpinner = ({ size = 'md', label = 'Loading' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-3',
    lg: 'w-10 h-10 border-4',
  };

  return (
    <div
      className={cn('loading-spinner', sizeClasses[size])}
      role="status"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, description, action }) => {
  return (
    <div className="empty-state" role="status">
      {Icon && <Icon className="empty-state-icon" aria-hidden="true" />}
      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4 max-w-md">{description}</p>
      {action}
    </div>
  );
};

export const StatCard = ({ icon: Icon, label, value, trend, trendUp, onClick }) => {
  return (
    <div
      className={cn("stat-card", onClick && "cursor-pointer")}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${label}: ${value}` : undefined}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center" aria-hidden="true">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        {trend && (
          <span className={cn(
            'text-sm font-medium',
            trendUp ? 'text-accent' : 'text-destructive'
          )}>
            {trendUp ? '+' : ''}{trend}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <p className="text-3xl font-heading font-bold text-foreground">{value}</p>
    </div>
  );
};
